import { useState, useRef, useEffect } from 'react';
import { Menu as MenuIcon, X } from 'lucide-react';
import { Usuario } from '../types/usuario';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Input } from './Input';
import { Button } from './Button';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useUsuarios } from '../hooks/useUsuarios';
import { toast } from 'react-toastify';
import { MessageModal } from './MessageModal';
import { useSaldo } from '../contexts/SaldoContext';
import { ConfirmationModal } from './ConfirmationModal';
import { supabase } from '../lib/supabase';

interface UserMenuProps {
  usuario: Usuario;
  onAfterAction?: () => void;
}

interface AdicionarSaldoModalProps {
  isOpen: boolean;
  onClose: () => void;
  usuario: Usuario;
}

// Componente do Modal para Adicionar Saldo
const AdicionarSaldoModal = ({ isOpen, onClose, usuario }: AdicionarSaldoModalProps) => {
  const [saldo, setSaldo] = useState<number>(0);
  const [observacao, setObservacao] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { saldo: saldoUsuarioLogado, atualizarSaldo } = useSaldo();
  
  if (!isOpen || !user) return null;
  
  const handleAdicionarSaldo = async () => {
    if (saldo === 0) {
      setErrorMessage('O valor do saldo não pode ser zero');
      setErrorModalOpen(true);
      return;
    }
    
    const isValorNegativo = saldo < 0;
    const valorAbsoluto = Math.abs(saldo);
    
    // Se valor é positivo (adição), verificar se usuário logado tem saldo suficiente
    if (!isValorNegativo && saldoUsuarioLogado < valorAbsoluto) {
      setErrorMessage('Saldo insuficiente para realizar esta operação');
      setErrorModalOpen(true);
      return;
    }
    
    // Se valor é negativo (subtração), verificar se usuário de destino terá saldo suficiente
    if (isValorNegativo && (usuario.saldo || 0) < valorAbsoluto) {
      setErrorMessage(`Saldo insuficiente no usuário ${usuario.nome} para realizar esta operação. Saldo atual: ${usuario.saldo || 0}`);
      setErrorModalOpen(true);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // A transferência acontece no servidor (função `transferir_saldo`): confere a
      // carteira e o saldo, move os créditos e faz o lançamento em `etiquetas` numa
      // única transação. Valor positivo envia ao usuário; negativo recupera dele.
      const { data: novoSaldoUsuarioLogado, error } = await supabase.rpc('transferir_saldo', {
        p_destino: usuario.id,
        p_quantidade: saldo,
        p_observacao: observacao || null,
      });

      if (error) throw error;

      // Atualizar o contexto de saldo para refletir imediatamente na UI
      atualizarSaldo(novoSaldoUsuarioLogado as number);
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      queryClient.invalidateQueries({ queryKey: ['etiquetas'] });

      const operacao = isValorNegativo ? 'subtraído' : 'adicionado';
      const preposicao = isValorNegativo ? 'de' : 'para';
      toast.success(`Saldo de ${valorAbsoluto} ${operacao} com sucesso ${preposicao} ${usuario.nome}`);
      setSaldo(0);
      setObservacao('');
      onClose();
    } catch (error: any) {
      setErrorMessage(`Erro ao processar saldo: ${error.message}`);
      setErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <>
      {createPortal(
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
          
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <X size={20} />
              </button>
              
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Gerenciar Saldo para {usuario.nome}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
                  <Input
                    type="number"
                    value={saldo.toString()}
                    onChange={(e) => setSaldo(Number(e.target.value))}
                    required
                    placeholder="Digite um valor positivo para adicionar ou negativo para subtrair"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Seu saldo atual: {saldoUsuarioLogado} | Saldo do usuário: {usuario.saldo || 0}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    💡 Dica: Use valores positivos para adicionar saldo e negativos para subtrair
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
                  <Input
                    type="text"
                    value={observacao}
                    onChange={(e) => setObservacao(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <Button
                  onClick={onClose}
                  className="bg-gray-500 hover:bg-gray-600"
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAdicionarSaldo}
                  isLoading={isLoading}
                >
                  Confirmar
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      <MessageModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title="Atenção"
        message={errorMessage}
      />
    </>
  );
};

export function UserMenu({ usuario, onAfterAction }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaldoModalOpen, setIsSaldoModalOpen] = useState(false);
  const [isBloquearModalOpen, setIsBloquearModalOpen] = useState(false);
  const [isDesbloquearModalOpen, setIsDesbloquearModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { atualizarUsuario } = useUsuarios();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (isOpen && 
          menuRef.current && !menuRef.current.contains(event.target as Node) && 
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleBloquearUsuario = async () => {
    setIsLoading(true);
    try {
      await atualizarUsuario({
        id: usuario.id,
        usuario: { ativo: false }
      });
      toast.success(`Usuário ${usuario.nome} bloqueado com sucesso!`);
      setIsBloquearModalOpen(false);
      // Atualiza o objeto usuario localmente para refletir a mudança na UI
      usuario.ativo = false;
      
      // Chama o callback se existir
      if (onAfterAction) {
        onAfterAction();
      }
    } catch (error: any) {
      toast.error(`Erro ao bloquear usuário: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDesbloquearUsuario = async () => {
    setIsLoading(true);
    try {
      // Verifica se o tipo do usuário é 'novo' e atualiza para 'usuario' ao desbloquear
      const updateData = { 
        ativo: true,
        ...(usuario.tipo === 'novo' ? { tipo: 'usuario' } : {})
      };
      
      await atualizarUsuario({
        id: usuario.id,
        usuario: updateData
      });
      
      toast.success(`Usuário ${usuario.nome} desbloqueado com sucesso!`);
      setIsDesbloquearModalOpen(false);
      
      // Atualiza o objeto usuario localmente para refletir a mudança na UI
      usuario.ativo = true;
      if (usuario.tipo === 'novo') {
        usuario.tipo = 'usuario';
      }
      
      // Chama o callback se existir
      if (onAfterAction) {
        onAfterAction();
      }
    } catch (error: any) {
      toast.error(`Erro ao desbloquear usuário: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Verifica se o usuário está bloqueado
  const isUsuarioBloqueado = usuario.ativo === false;

  return (
    <>
      <div className="relative">
        <button 
          ref={buttonRef}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className="text-gray-600 hover:text-gray-900 flex items-center justify-center w-5 h-5"
        >
          <MenuIcon size={20} />
        </button>
        
        {isOpen && createPortal(
          <div 
            ref={menuRef}
            className="fixed w-48 bg-white rounded-lg shadow-lg py-1 z-[9999] user-menu-portal"
            style={{ 
              maxWidth: 'calc(100vw - 20px)',
              top: buttonRef.current ? buttonRef.current.getBoundingClientRect().bottom + 5 : 0,
              right: buttonRef.current ? window.innerWidth - buttonRef.current.getBoundingClientRect().right : 0,
              backgroundColor: 'white',
              opacity: 1,
              backdropFilter: 'none',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)'
            }}
          >
            <button 
              onClick={() => {
                setIsSaldoModalOpen(true);
                setIsOpen(false);
              }}
              className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
            >
              Gerenciar Saldo
            </button>
            <button 
              onClick={() => {
                navigate(`/usuarios/${usuario.id}/historico`);
                setIsOpen(false);
              }}
              className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
            >
              Ver Histórico
            </button>
            {isUsuarioBloqueado ? (
              <button 
                onClick={() => {
                  setIsDesbloquearModalOpen(true);
                  setIsOpen(false);
                }}
                className="block w-full px-4 py-2 text-left text-green-600 hover:bg-gray-100"
              >
                Desbloquear Usuário
              </button>
            ) : (
              <button 
                onClick={() => {
                  setIsBloquearModalOpen(true);
                  setIsOpen(false);
                }}
                className="block w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100"
              >
                Bloquear Usuário
              </button>
            )}
          </div>,
          document.body
        )}
      </div>
      
      <AdicionarSaldoModal 
        isOpen={isSaldoModalOpen}
        onClose={() => setIsSaldoModalOpen(false)}
        usuario={usuario}
      />

      <ConfirmationModal
        isOpen={isBloquearModalOpen}
        onClose={() => setIsBloquearModalOpen(false)}
        onConfirm={handleBloquearUsuario}
        title="Bloquear Usuário"
        message={`Tem certeza que deseja bloquear o usuário ${usuario.nome}? Usuários bloqueados não poderão acessar o sistema.`}
        confirmText="Bloquear"
        cancelText="Cancelar"
        isLoading={isLoading}
      />

      <ConfirmationModal
        isOpen={isDesbloquearModalOpen}
        onClose={() => setIsDesbloquearModalOpen(false)}
        onConfirm={handleDesbloquearUsuario}
        title="Desbloquear Usuário"
        message={`Tem certeza que deseja desbloquear o usuário ${usuario.nome}? Usuários desbloqueados poderão acessar o sistema normalmente.`}
        confirmText="Desbloquear"
        cancelText="Cancelar"
        confirmButtonColor="bg-green-600"
        confirmButtonHoverColor="hover:bg-green-700"
        isLoading={isLoading}
      />
    </>
  );
} 